import { inject, Injectable } from '@angular/core';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { PlayerTransfer } from '@models';
import { DeepPartial } from '@models';
import { combineLatest, map, of, shareReplay, switchMap } from 'rxjs';
import { ClubsService } from './club.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { TenantContextService } from './tenant-context.service';
import { UserService } from './current-user.service';

@Injectable({
  providedIn: 'root',
})
export class TransferService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly seasonManager = inject(SeasonManagerService);
  private readonly userService = inject(UserService);
  private readonly clubsService = inject(ClubsService);
  private readonly functions = inject(Functions);

  private get col() {
    return this.tenantContext.collections.TRANSFERS;
  }

  public all$ = this.seasonManager.season$.pipe(
    switchMap((season) =>
      this.firestore.listDocuments<PlayerTransfer>(
        this.col,
        where('seasonYear', '==', season),
        orderBy('createdAt', 'desc'),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public myPlayersTransfersRequests$ = this.userService.user$.pipe(
    switchMap((user) =>
      this.firestore.listDocuments(
        this.tenantContext.collections.CLUBS,
        where('managerId', '==', user.id),
      ),
    ),
    switchMap((clubs) => {
      if (!clubs || clubs.length === 0) return of([]);
      const clubIds = clubs.map((c: any) => c.id);
      const chunks: string[][] = [];
      for (let i = 0; i < clubIds.length; i += 15) {
        chunks.push(clubIds.slice(i, i + 15));
      }
      return combineLatest(
        chunks.map((chunk) =>
          this.firestore.listDocuments<PlayerTransfer>(
            this.col,
            where('toClubId', 'in', chunk),
            where('status', '==', 'PENDING'),
            where('seasonYear', '==', this.seasonManager.getActualSeason()),
          ),
        ),
      ).pipe(map((results) => results.flat()));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  get(id: string) {
    return this.firestore.getDocument<PlayerTransfer>(this.col, id);
  }

  create(transfer: Omit<PlayerTransfer, 'id'>) {
    return this.firestore
      .create<PlayerTransfer>(this.col, transfer)
      .then(async (id) => {
        try {
          const fn = httpsCallable<
            { transferId: string },
            { success: boolean }
          >(this.functions, 'notifyTransferCreated');
          await fn({ transferId: id });
        } catch {
          // Notification failure must not block the transfer creation
        }
        return id;
      });
  }

  update(id: string, partial: DeepPartial<PlayerTransfer>) {
    return this.firestore.update<PlayerTransfer>(this.col, {
      id,
      ...partial,
    } as Partial<PlayerTransfer>);
  }

  accept(transferId: string) {
    const callable = httpsCallable(
      this.functions,
      'answerPlayerTransferRequest',
    );
    return callable({ transferId, answer: 'ACCEPTED' });
  }

  reject(transferId: string) {
    const callable = httpsCallable(
      this.functions,
      'answerPlayerTransferRequest',
    );
    return callable({ transferId, answer: 'REJECTED' });
  }
}
